import React, { useState, useEffect, useCallback } from 'react';
import {
  Gift, Settings, BarChart3, Mail, Trophy, Users, Upload, Copy, CheckCircle,
  Plus, Pencil, Trash2, Eye, Calendar, Target, Award, Send, RefreshCw,
  ToggleLeft, ToggleRight, Image, Link, FileText, X, ChevronDown, ChevronUp,
  Monitor, Tablet, Smartphone, Share2, Zap, Megaphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line
} from 'recharts';
import EmailBlockBuilder from '@/components/EmailBlockBuilder';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReferralManagement - Admin dashboard component for managing the referral system
 */
const ReferralManagement = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  
  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    mode: 'general',
    share_image_url: '',           // OG image for social media (1200x630)
    banner_desktop_url: '',        // In-app banner desktop (1200x400)
    banner_mobile_url: '',         // In-app banner mobile (600x300)
    share_preview_color: '#1a1a2e', // Preview card background color
    share_title: 'Join me on UnCloned Journal!',
    share_description: 'Start your daily journaling journey.'
  });
  
  // Preview device state for in-app banner preview
  const [previewDevice, setPreviewDevice] = useState('desktop');
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    prize_name: '',
    prize_image_url: '',
    referrals_needed: 3,
    start_date: '',
    end_date: '',
    rules: ['Referrals must create an account', 'Referrals must complete at least one journal entry'],
    custom_share_message: ''
  });
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Winner selection state
  const [selectedCampaignForWinner, setSelectedCampaignForWinner] = useState(null);
  const [winnerCount, setWinnerCount] = useState(1);
  const [recentWinners, setRecentWinners] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/settings`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/campaigns`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/analytics`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  const fetchEmailTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/email-templates`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      const data = await response.json();
      if (data.success) {
        setEmailTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchCampaigns(), fetchAnalytics(), fetchEmailTemplates()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchCampaigns, fetchAnalytics, fetchEmailTemplates]);

  const updateSettings = async (updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': localStorage.getItem('adminToken')
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
        toast.success('Settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleShareImageUpload = async (e, imageType = 'share') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType); // 'share', 'banner_desktop', or 'banner_mobile'
    
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/settings/upload-share-image`, {
        method: 'POST',
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        // Update the appropriate field based on image type
        const fieldMap = {
          'share': 'share_image_url',
          'banner_desktop': 'banner_desktop_url',
          'banner_mobile': 'banner_mobile_url'
        };
        const field = fieldMap[imageType] || 'share_image_url';
        setSettings(prev => ({ ...prev, [field]: data.image_url }));
        toast.success(`${imageType === 'share' ? 'Social share' : imageType === 'banner_desktop' ? 'Desktop banner' : 'Mobile banner'} image uploaded`);
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handlePrizeImageUpload = async (e, campaignId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (campaignId) {
      // Upload to existing campaign
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch(`${API_URL}/api/admin/referral/campaigns/${campaignId}/upload-prize-image`, {
          method: 'POST',
          headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
          body: formData
        });
        const data = await response.json();
        if (data.success) {
          await fetchCampaigns();
          toast.success('Prize image uploaded');
        }
      } catch (error) {
        toast.error('Failed to upload image');
      }
    } else {
      // Preview for new campaign
      const reader = new FileReader();
      reader.onload = (e) => {
        setCampaignForm(prev => ({ ...prev, prize_image_url: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const saveCampaign = async () => {
    try {
      const url = editingCampaign 
        ? `${API_URL}/api/admin/referral/campaigns/${editingCampaign.campaign_id}`
        : `${API_URL}/api/admin/referral/campaigns`;
      
      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': localStorage.getItem('adminToken')
        },
        body: JSON.stringify(campaignForm)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(editingCampaign ? 'Campaign updated' : 'Campaign created');
        setShowCampaignModal(false);
        setEditingCampaign(null);
        setCampaignForm({
          name: '',
          prize_name: '',
          prize_image_url: '',
          referrals_needed: 3,
          start_date: '',
          end_date: '',
          rules: ['Referrals must create an account', 'Referrals must complete at least one journal entry'],
          custom_share_message: ''
        });
        await fetchCampaigns();
      }
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Campaign deleted');
        await fetchCampaigns();
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const toggleCampaignActive = async (campaign) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/campaigns/${campaign.campaign_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': localStorage.getItem('adminToken')
        },
        body: JSON.stringify({ is_active: !campaign.is_active })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(campaign.is_active ? 'Campaign deactivated' : 'Campaign activated');
        await fetchCampaigns();
      }
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const drawWinner = async () => {
    if (!selectedCampaignForWinner) {
      toast.error('Please select a campaign');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/referral/campaigns/${selectedCampaignForWinner}/draw-winner?count=${winnerCount}`,
        {
          method: 'POST',
          headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
        }
      );
      const data = await response.json();
      if (data.success && data.winners?.length > 0) {
        setRecentWinners(data.winners);
        toast.success(`Selected ${data.winners.length} winner(s)!`);
        await fetchCampaigns();
      } else {
        toast.error(data.error || 'No eligible users to select');
      }
    } catch (error) {
      toast.error('Failed to draw winner');
    }
  };

  const notifyWinners = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/campaigns/${campaignId}/notify-winners`, {
        method: 'POST',
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Notified ${data.sent_count} winner(s)`);
      } else {
        toast.error(data.error || 'Failed to notify winners');
      }
    } catch (error) {
      toast.error('Failed to send notifications');
    }
  };

  const updateEmailTemplate = async (templateType, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/email-templates/${templateType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': localStorage.getItem('adminToken')
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template updated');
        await fetchEmailTemplates();
        setEditingTemplate(null);
      }
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  // Test email modal state
  const [testEmailModal, setTestEmailModal] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [togglingTemplate, setTogglingTemplate] = useState(null);
  const [emailPreviewDevice, setEmailPreviewDevice] = useState('desktop');

  const sendTestEmail = async () => {
    if (!testEmailModal || !testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      setSendingTest(true);
      const response = await fetch(`${API_URL}/api/admin/referral/email-templates/${testEmailModal}/test`, {
        method: 'POST',
        headers: { 
          'X-Admin-Token': localStorage.getItem('adminToken'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: testEmailAddress })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Test email sent to ${testEmailAddress}!`);
        setTestEmailModal(null);
        setTestEmailAddress('');
      } else {
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleEmailTemplate = async (template) => {
    const newEnabled = !template.enabled;
    setTogglingTemplate(template.template_type);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/referral/email-templates/${template.template_type}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': localStorage.getItem('adminToken')
        },
        body: JSON.stringify({ enabled: newEnabled })
      });
      const data = await response.json();
      if (data.success) {
        await fetchEmailTemplates();
        toast.success(`${template.name} ${newEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error('Failed to update template status');
    } finally {
      setTogglingTemplate(null);
    }
  };

  // Render email preview matching SystemEmailTemplates style
  const renderReferralEmailPreview = () => {
    const bodyHtml = editingTemplate?.body_html || '';
    const cleanHtml = bodyHtml.replace(/<!-- BLOCKS:.*? -->\n?/g, '');
    
    const previewHtml = cleanHtml
      .replace(/\{\{first_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">John</span>')
      .replace(/\{\{referrer_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Jane</span>')
      .replace(/\{\{referral_link\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">https://unclonedjournal.com/ref/abc123</span>')
      .replace(/\{\{referral_count\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">5</span>')
      .replace(/\{\{prize_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">iPad Pro</span>')
      .replace(/\{\{campaign_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">August Giveaway</span>')
      .replace(/\{\{primary_color\}\}/g, '#df1283');
    
    return (
      <div className={`mx-auto transition-all duration-300 ${
        emailPreviewDevice === 'mobile' ? 'max-w-[320px]' : 
        emailPreviewDevice === 'tablet' ? 'max-w-[400px]' : 
        'max-w-full'
      }`}>
        {emailPreviewDevice !== 'desktop' && (
          <div className="bg-gray-800 rounded-[2rem] p-2 shadow-xl">
            <div className="bg-gray-800 rounded-[1.5rem] overflow-hidden">
              {emailPreviewDevice === 'mobile' && (
                <div className="h-6 bg-gray-800 flex items-center justify-center">
                  <div className="w-20 h-4 bg-black rounded-full"></div>
                </div>
              )}
              <div className="bg-white overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-[8px] font-bold">UJ</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-xs truncate">UnCloned® Journal</p>
                      <p className="text-[10px] text-gray-500 truncate">info+journal@uncloned...</p>
                    </div>
                  </div>
                </div>
                <div className="px-3 py-2 border-b bg-white">
                  <p className="font-semibold text-gray-900 text-xs truncate">{editingTemplate?.subject || 'Subject line...'}</p>
                </div>
                <div className="p-3 max-h-[400px] overflow-y-auto">
                  {previewHtml ? (
                    <div className="max-w-none text-gray-700 email-preview-content" style={{ fontSize: emailPreviewDevice === 'mobile' ? '12px' : '13px', lineHeight: '1.5' }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
                  ) : (
                    <p className="text-gray-400 italic text-xs">Your email content...</p>
                  )}
                </div>
                <div className="px-3 py-2 bg-gray-50 border-t text-center">
                  <p className="text-[10px] text-gray-400">Sent with love from UnCloned®</p>
                </div>
              </div>
              {emailPreviewDevice === 'mobile' && (
                <div className="h-5 bg-gray-800 flex items-center justify-center">
                  <div className="w-24 h-1 bg-white rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {emailPreviewDevice === 'desktop' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-xs font-bold">UJ</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">UnCloned® Journal</p>
                  <p className="text-xs text-gray-500 truncate">info+journal@unclonedmarketing.com</p>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-b bg-white">
              <p className="font-semibold text-gray-900 truncate">{editingTemplate?.subject || 'Subject line here...'}</p>
            </div>
            <div className="p-4 min-h-[300px] max-h-[500px] overflow-y-auto">
              {previewHtml ? (
                <div className="max-w-none text-gray-700 email-preview-content" style={{ fontSize: '14px', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
              ) : (
                <p className="text-gray-400 italic">Start writing your email content...</p>
              )}
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t text-center">
              <p className="text-xs text-gray-400">Sent with love from UnCloned® • © 2026 UnCloned®. All rights reserved.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  const tabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'campaigns', label: 'Campaigns', icon: Trophy },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'winners', label: 'Winners', icon: Award },
    { id: 'emails', label: 'Email Templates', icon: Mail }
  ];

  return (
    <div className="space-y-6" data-testid="referral-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #df1283 0%, #FF7901 100%)' }}>
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Referral System</h2>
            <p className="text-sm text-white/60">Manage campaigns, analytics, and rewards</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/60">System Status:</span>
          <button
            onClick={() => updateSettings({ enabled: !settings.enabled })}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              settings.enabled 
                ? 'bg-[#0FFFE2]/20 text-[#0FFFE2] border border-[#0FFFE2]/30' 
                : 'bg-white/5 text-white/50 border border-white/10'
            }`}
            data-testid="referral-system-toggle"
          >
            {settings.enabled ? 'Active' : 'Inactive'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-[#df1283]/20 text-[#df1283] border border-[#df1283]/30'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
            data-testid={`referral-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="glass-card p-6">
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6" data-testid="referral-settings-panel">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <h3 className="text-white font-medium">Referral Mode</h3>
                <p className="text-sm text-white/60">Choose between promotional campaigns or general sharing</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => updateSettings({ mode: 'general' })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    settings.mode === 'general'
                      ? 'bg-[#0FFFE2] text-black'
                      : 'bg-white/10 text-white/60 hover:text-white'
                  }`}
                >
                  General
                </button>
                <button
                  onClick={() => updateSettings({ mode: 'promotional' })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    settings.mode === 'promotional'
                      ? 'bg-[#df1283] text-white'
                      : 'bg-white/10 text-white/60 hover:text-white'
                  }`}
                >
                  Promotional
                </button>
              </div>
            </div>

            {/* Mode-Specific Description */}
            <div className={`p-4 rounded-xl border ${
              settings.mode === 'general' 
                ? 'bg-[#0FFFE2]/10 border-[#0FFFE2]/30' 
                : 'bg-[#df1283]/10 border-[#df1283]/30'
            }`}>
              {settings.mode === 'general' ? (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#0FFFE2]/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#0FFFE2]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">General Referral Mode</h4>
                    <p className="text-sm text-white/60 mt-1">
                      Simple, always-on referral system. Users can share their unique link anytime to invite friends. 
                      Track referrals and reward users based on total invites — no deadlines or campaign limits.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs bg-[#0FFFE2]/20 text-[#0FFFE2] px-2 py-1 rounded">Always Active</span>
                      <span className="text-xs bg-[#0FFFE2]/20 text-[#0FFFE2] px-2 py-1 rounded">No Deadlines</span>
                      <span className="text-xs bg-[#0FFFE2]/20 text-[#0FFFE2] px-2 py-1 rounded">Milestone Rewards</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#df1283]/20 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-[#df1283]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Promotional Campaign Mode</h4>
                    <p className="text-sm text-white/60 mt-1">
                      Run time-limited referral campaigns with specific prizes and goals. Create urgency with deadlines, 
                      announce winners, and drive viral growth with exciting giveaways.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs bg-[#df1283]/20 text-[#df1283] px-2 py-1 rounded">Time-Limited</span>
                      <span className="text-xs bg-[#df1283]/20 text-[#df1283] px-2 py-1 rounded">Prize Giveaways</span>
                      <span className="text-xs bg-[#df1283]/20 text-[#df1283] px-2 py-1 rounded">Winner Selection</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* GENERAL MODE SETTINGS */}
            {settings.mode === 'general' && (
              <div className="space-y-6">
                {/* Reward Milestones */}
                <div className="p-4 bg-white/5 rounded-lg space-y-4 border border-[#0FFFE2]/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#0FFFE2]" />
                      <h4 className="text-white font-medium">Referral Milestones</h4>
                    </div>
                    <span className="text-xs text-white/40">Reward users when they hit these targets</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-[#0FFFE2]/20 flex items-center justify-center mb-2">
                        <span className="text-[#0FFFE2] font-bold">3</span>
                      </div>
                      <p className="text-sm text-white font-medium">Bronze</p>
                      <p className="text-xs text-white/50">3 referrals</p>
                      <input
                        type="text"
                        placeholder="e.g., Free Month"
                        value={settings.milestone_bronze_reward || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, milestone_bronze_reward: e.target.value }))}
                        onBlur={() => updateSettings({ milestone_bronze_reward: settings.milestone_bronze_reward })}
                        className="mt-2 w-full px-2 py-1 text-xs bg-white/10 border border-white/20 rounded text-white text-center"
                      />
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-[#FF7901]/20 flex items-center justify-center mb-2">
                        <span className="text-[#FF7901] font-bold">10</span>
                      </div>
                      <p className="text-sm text-white font-medium">Silver</p>
                      <p className="text-xs text-white/50">10 referrals</p>
                      <input
                        type="text"
                        placeholder="e.g., Lifetime Access"
                        value={settings.milestone_silver_reward || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, milestone_silver_reward: e.target.value }))}
                        onBlur={() => updateSettings({ milestone_silver_reward: settings.milestone_silver_reward })}
                        className="mt-2 w-full px-2 py-1 text-xs bg-white/10 border border-white/20 rounded text-white text-center"
                      />
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-[#df1283]/20 flex items-center justify-center mb-2">
                        <span className="text-[#df1283] font-bold">25</span>
                      </div>
                      <p className="text-sm text-white font-medium">Gold</p>
                      <p className="text-xs text-white/50">25 referrals</p>
                      <input
                        type="text"
                        placeholder="e.g., VIP Status"
                        value={settings.milestone_gold_reward || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, milestone_gold_reward: e.target.value }))}
                        onBlur={() => updateSettings({ milestone_gold_reward: settings.milestone_gold_reward })}
                        className="mt-2 w-full px-2 py-1 text-xs bg-white/10 border border-white/20 rounded text-white text-center"
                      />
                    </div>
                  </div>
                </div>

                {/* General Share Settings */}
                <div className="p-4 bg-white/5 rounded-lg space-y-4">
                  <div className="flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-[#0FFFE2]" />
                    <h4 className="text-white font-medium">Share Link Settings</h4>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-white/70 block mb-1">Default Share Message</label>
                      <textarea
                        value={settings.general_share_message || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, general_share_message: e.target.value }))}
                        onBlur={() => updateSettings({ general_share_message: settings.general_share_message })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none text-sm"
                        rows={3}
                        placeholder="Hey! I've been using UnCloned Journal and it's amazing. Use my link to join: {{referral_link}}"
                      />
                      <p className="text-xs text-white/40 mt-1">Use {'{{referral_link}}'} to insert the user&apos;s unique link</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-white/70 block mb-1">Referral Bonus (for referrer)</label>
                        <input
                          type="text"
                          value={settings.referrer_bonus || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, referrer_bonus: e.target.value }))}
                          onBlur={() => updateSettings({ referrer_bonus: settings.referrer_bonus })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          placeholder="e.g., 1 week free"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-white/70 block mb-1">Signup Bonus (for new user)</label>
                        <input
                          type="text"
                          value={settings.referee_bonus || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, referee_bonus: e.target.value }))}
                          onBlur={() => updateSettings({ referee_bonus: settings.referee_bonus })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          placeholder="e.g., 10% off first month"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PROMOTIONAL MODE SETTINGS */}
            {settings.mode === 'promotional' && (
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="p-4 bg-white/5 rounded-lg border border-[#df1283]/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#df1283]" />
                      <h4 className="text-white font-medium">Quick Actions</h4>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    <button 
                      onClick={() => setActiveTab('campaigns')}
                      className="p-3 bg-[#df1283]/20 hover:bg-[#df1283]/30 rounded-lg text-center transition-colors"
                    >
                      <Trophy className="w-6 h-6 text-[#df1283] mx-auto mb-1" />
                      <p className="text-sm text-white">Manage Campaigns</p>
                      <p className="text-xs text-white/50">{campaigns.length} active</p>
                    </button>
                    <button 
                      onClick={() => setActiveTab('winners')}
                      className="p-3 bg-[#FF7901]/20 hover:bg-[#FF7901]/30 rounded-lg text-center transition-colors"
                    >
                      <Award className="w-6 h-6 text-[#FF7901] mx-auto mb-1" />
                      <p className="text-sm text-white">View Winners</p>
                      <p className="text-xs text-white/50">Select & announce</p>
                    </button>
                    <button 
                      onClick={() => setActiveTab('analytics')}
                      className="p-3 bg-[#0FFFE2]/20 hover:bg-[#0FFFE2]/30 rounded-lg text-center transition-colors"
                    >
                      <BarChart3 className="w-6 h-6 text-[#0FFFE2] mx-auto mb-1" />
                      <p className="text-sm text-white">Analytics</p>
                      <p className="text-xs text-white/50">Track performance</p>
                    </button>
                    <button 
                      onClick={() => setActiveTab('emails')}
                      className="p-3 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-center transition-colors"
                    >
                      <Mail className="w-6 h-6 text-purple-400 mx-auto mb-1" />
                      <p className="text-sm text-white">Email Templates</p>
                      <p className="text-xs text-white/50">{emailTemplates.length} templates</p>
                    </button>
                  </div>
                </div>

                {/* Campaign Promotion Settings */}
                <div className="p-4 bg-white/5 rounded-lg space-y-4">
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-[#df1283]" />
                    <h4 className="text-white font-medium">Campaign Display Settings</h4>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-sm text-white">Show campaign banner in app</p>
                        <p className="text-xs text-white/50">Display active campaign promotion to all users</p>
                      </div>
                      <Switch
                        checked={settings.show_campaign_banner !== false}
                        onCheckedChange={(checked) => updateSettings({ show_campaign_banner: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-sm text-white">Show countdown timer</p>
                        <p className="text-xs text-white/50">Display urgency timer for ending campaigns</p>
                      </div>
                      <Switch
                        checked={settings.show_countdown !== false}
                        onCheckedChange={(checked) => updateSettings({ show_countdown: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-sm text-white">Show leaderboard</p>
                        <p className="text-xs text-white/50">Display top referrers during campaign</p>
                      </div>
                      <Switch
                        checked={settings.show_leaderboard !== false}
                        onCheckedChange={(checked) => updateSettings({ show_leaderboard: checked })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Share Customization - Common to both modes */}
            <div className="space-y-6">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Link className="w-4 h-4" /> Social Share & In-App Banner
              </h3>
              <p className="text-sm text-white/60">Configure images for social media previews and in-app display</p>
              
              {/* Section 1: Social Media OG Image */}
              <div className="p-4 bg-white/5 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-[#df1283]" />
                  <h4 className="text-white font-medium">Social Media Preview (OG Image)</h4>
                </div>
                <p className="text-xs text-white/50">This image appears when someone shares your referral link on Facebook, Twitter, LinkedIn, etc.</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm text-white/70">OG Image <span className="text-white/40">(1200×630px)</span></label>
                    <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-[#df1283]/50 transition-colors">
                      {settings.share_image_url ? (
                        <div className="relative">
                          <img 
                            src={settings.share_image_url} 
                            alt="Social share preview" 
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => updateSettings({ share_image_url: '' })}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="w-8 h-8 mx-auto text-white/40 mb-2" />
                          <span className="text-sm text-white/60">Upload OG Image</span>
                          <p className="text-xs text-white/40 mt-1">1200×630px recommended</p>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleShareImageUpload(e, 'share')}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-white/70 block mb-1">Share Title</label>
                      <input
                        type="text"
                        value={settings.share_title}
                        onChange={(e) => setSettings(prev => ({ ...prev, share_title: e.target.value }))}
                        onBlur={() => updateSettings({ share_title: settings.share_title })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        placeholder="Join me on UnCloned Journal!"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/70 block mb-1">Share Description</label>
                      <textarea
                        value={settings.share_description}
                        onChange={(e) => setSettings(prev => ({ ...prev, share_description: e.target.value }))}
                        onBlur={() => updateSettings({ share_description: settings.share_description })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
                        rows={2}
                        placeholder="Start your daily journaling journey."
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/70 block mb-1">Preview Card Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={settings.share_preview_color || '#1a1a2e'}
                          onChange={(e) => setSettings(prev => ({ ...prev, share_preview_color: e.target.value }))}
                          onBlur={() => updateSettings({ share_preview_color: settings.share_preview_color })}
                          className="w-10 h-10 rounded-lg cursor-pointer border border-white/20"
                        />
                        <input
                          type="text"
                          value={settings.share_preview_color || '#1a1a2e'}
                          onChange={(e) => setSettings(prev => ({ ...prev, share_preview_color: e.target.value }))}
                          onBlur={() => updateSettings({ share_preview_color: settings.share_preview_color })}
                          className="w-28 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-mono"
                          placeholder="#1a1a2e"
                        />
                        <span className="text-xs text-white/40">Background color for social preview card</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Social Media Preview Card */}
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-white/50 mb-2">Live Preview (as seen on social media):</p>
                  <div 
                    className="rounded-lg overflow-hidden max-w-md shadow-lg"
                    style={{ backgroundColor: settings.share_preview_color || '#1a1a2e' }}
                  >
                    {settings.share_image_url && (
                      <img src={settings.share_image_url} alt="Preview" className="w-full h-32 object-cover" />
                    )}
                    <div className="p-3">
                      <p className="text-white font-medium text-sm">{settings.share_title || 'Join me on UnCloned Journal!'}</p>
                      <p className="text-white/60 text-xs mt-1">{settings.share_description || 'Start your daily journaling journey.'}</p>
                      <p className="text-[#0FFFE2] text-xs mt-1">unclonedjournal.com</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section 2: In-App Banner */}
              <div className="p-4 bg-white/5 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-[#FF7901]" />
                  <h4 className="text-white font-medium">In-App Referral Banner</h4>
                </div>
                <p className="text-xs text-white/50">This banner appears inside the app when users view referral promotions. Upload separate images for desktop and mobile for the best experience.</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Desktop Banner - Horizontal/Landscape */}
                  <div className="space-y-2">
                    <label className="text-sm text-white/70 flex items-center gap-2">
                      <Monitor className="w-4 h-4" /> Desktop Banner <span className="text-white/40">(1200×400px)</span>
                    </label>
                    <p className="text-xs text-white/40 -mt-1">Horizontal/Landscape orientation</p>
                    <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-[#FF7901]/50 transition-colors">
                      {settings.banner_desktop_url ? (
                        <div className="relative">
                          <img 
                            src={settings.banner_desktop_url} 
                            alt="Desktop banner preview" 
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => updateSettings({ banner_desktop_url: '' })}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="w-6 h-6 mx-auto text-white/40 mb-1" />
                          <span className="text-sm text-white/60">Upload Desktop Banner</span>
                          <p className="text-xs text-white/40">3:1 aspect ratio (wide)</p>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleShareImageUpload(e, 'banner_desktop')}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Banner - Vertical/Portrait */}
                  <div className="space-y-2">
                    <label className="text-sm text-white/70 flex items-center gap-2">
                      <Smartphone className="w-4 h-4" /> Mobile Banner <span className="text-white/40">(600×900px)</span>
                    </label>
                    <p className="text-xs text-white/40 -mt-1">Vertical/Portrait orientation</p>
                    <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-[#0FFFE2]/50 transition-colors">
                      {settings.banner_mobile_url ? (
                        <div className="relative flex justify-center">
                          <img 
                            src={settings.banner_mobile_url} 
                            alt="Mobile banner preview" 
                            className="h-32 w-auto object-contain rounded-lg"
                          />
                          <button
                            onClick={() => updateSettings({ banner_mobile_url: '' })}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="w-6 h-6 mx-auto text-white/40 mb-1" />
                          <span className="text-sm text-white/60">Upload Mobile Banner</span>
                          <p className="text-xs text-white/40">2:3 aspect ratio (tall)</p>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleShareImageUpload(e, 'banner_mobile')}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* In-App Banner Preview with Device Toggle */}
                {(settings.banner_desktop_url || settings.banner_mobile_url) && (
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-white/50">In-App Banner Preview:</p>
                      <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
                        <button
                          onClick={() => setPreviewDevice('desktop')}
                          className={`p-1.5 rounded ${previewDevice === 'desktop' ? 'bg-[#df1283] text-white' : 'text-white/50 hover:text-white'}`}
                          title="Desktop preview (horizontal)"
                        >
                          <Monitor className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPreviewDevice('tablet')}
                          className={`p-1.5 rounded ${previewDevice === 'tablet' ? 'bg-[#df1283] text-white' : 'text-white/50 hover:text-white'}`}
                          title="Tablet preview (horizontal)"
                        >
                          <Tablet className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPreviewDevice('mobile')}
                          className={`p-1.5 rounded ${previewDevice === 'mobile' ? 'bg-[#df1283] text-white' : 'text-white/50 hover:text-white'}`}
                          title="Mobile preview (vertical)"
                        >
                          <Smartphone className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Responsive Preview Container */}
                    <div className="flex justify-center">
                      {previewDevice === 'mobile' ? (
                        /* Mobile Preview - Vertical phone mockup */
                        <div className="bg-gray-800 rounded-3xl p-2 shadow-xl" style={{ width: '180px' }}>
                          <div className="bg-gray-900 rounded-2xl overflow-hidden" style={{ height: '320px' }}>
                            <img 
                              src={settings.banner_mobile_url || settings.banner_desktop_url} 
                              alt="Mobile preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      ) : (
                        /* Desktop/Tablet Preview - Horizontal */
                        <div 
                          className={`bg-gray-900 rounded-lg overflow-hidden transition-all duration-300 ${
                            previewDevice === 'desktop' ? 'w-full max-w-2xl' : 'w-80'
                          }`}
                        >
                          <img 
                            src={settings.banner_desktop_url || settings.banner_mobile_url} 
                            alt={`${previewDevice} preview`}
                            className="w-full h-auto object-cover"
                          />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-white/40 text-center mt-2">
                      {previewDevice === 'desktop' && 'Desktop view (1200px+ screens) — Horizontal banner'}
                      {previewDevice === 'tablet' && 'Tablet view (768px-1199px) — Uses desktop horizontal image'}
                      {previewDevice === 'mobile' && `Mobile view (<768px) — ${settings.banner_mobile_url ? 'Vertical banner for maximum visibility' : 'Falls back to desktop (will appear small)'}`}
                    </p>
                  </div>
                )}
                
                {/* Dimension Guide */}
                <div className="bg-white/5 rounded-lg p-3 mt-4">
                  <p className="text-xs text-white/70 font-medium mb-2">📐 Recommended Dimensions:</p>
                  <div className="grid grid-cols-2 gap-3 text-xs text-white/50">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-3 h-3 text-[#FF7901]" />
                        <span className="text-white/70 font-medium">Desktop (Horizontal)</span>
                      </div>
                      <p className="pl-5">1200×400px (3:1 wide)</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-3 h-3 text-[#0FFFE2]" />
                        <span className="text-white/70 font-medium">Mobile (Vertical)</span>
                      </div>
                      <p className="pl-5">600×900px (2:3 tall)</p>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-white/5 rounded text-xs text-white/50">
                    <p><strong>Why different orientations?</strong></p>
                    <p className="mt-1">• Desktop screens are wide → horizontal banners fill the space</p>
                    <p>• Mobile screens are tall → vertical banners are more prominent and engaging</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-4" data-testid="referral-campaigns-panel">
            <div className="flex justify-between items-center">
              <h3 className="text-white font-medium">Promotional Campaigns</h3>
              <Button
                onClick={() => {
                  setEditingCampaign(null);
                  setCampaignForm({
                    name: '',
                    prize_name: '',
                    prize_image_url: '',
                    referrals_needed: 3,
                    start_date: new Date().toISOString().split('T')[0],
                    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    rules: ['Referrals must create an account', 'Referrals must complete at least one journal entry'],
                    custom_share_message: ''
                  });
                  setShowCampaignModal(true);
                }}
                className="bg-[#df1283] hover:bg-[#F02D9A]"
                data-testid="create-campaign-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Campaign
              </Button>
            </div>

            {campaigns.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No campaigns yet. Create your first promotional campaign!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map(campaign => (
                  <div
                    key={campaign.campaign_id}
                    className={`p-4 rounded-lg border transition-all ${
                      campaign.is_active
                        ? 'bg-[#df1283]/10 border-[#df1283]/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {campaign.prize_image_url ? (
                          <img
                            src={campaign.prize_image_url}
                            alt={campaign.prize_name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center">
                            <Trophy className="w-8 h-8 text-[#df1283]" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium">{campaign.name}</h4>
                            {campaign.is_active && (
                              <span className="px-2 py-0.5 bg-[#0FFFE2]/20 text-[#0FFFE2] text-xs rounded-full">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-white/60">Prize: {campaign.prize_name}</p>
                          <p className="text-xs text-white/40 mt-1">
                            {campaign.start_date?.split('T')[0]} → {campaign.end_date?.split('T')[0]} • {campaign.referrals_needed} referrals needed
                          </p>
                          <div className="flex gap-4 mt-2 text-xs">
                            <span className="text-[#0FFFE2]">{campaign.total_referrals || 0} referrals</span>
                            <span className="text-[#FF7901]">{campaign.qualified_users || 0} qualified</span>
                            <span className="text-[#df1283]">{campaign.winners?.length || 0} winners</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleCampaignActive(campaign)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title={campaign.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {campaign.is_active ? (
                            <ToggleRight className="w-5 h-5 text-[#0FFFE2]" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-white/40" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingCampaign(campaign);
                            setCampaignForm({
                              name: campaign.name,
                              prize_name: campaign.prize_name,
                              prize_image_url: campaign.prize_image_url || '',
                              referrals_needed: campaign.referrals_needed,
                              start_date: campaign.start_date?.split('T')[0] || '',
                              end_date: campaign.end_date?.split('T')[0] || '',
                              rules: campaign.rules || [],
                              custom_share_message: campaign.custom_share_message || ''
                            });
                            setShowCampaignModal(true);
                          }}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4 text-white/60" />
                        </button>
                        <label className="p-2 hover:bg-white/10 rounded-lg transition-colors cursor-pointer" title="Upload Prize Image">
                          <Image className="w-4 h-4 text-white/60" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handlePrizeImageUpload(e, campaign.campaign_id)}
                          />
                        </label>
                        <button
                          onClick={() => deleteCampaign(campaign.campaign_id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6" data-testid="referral-analytics-panel">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-white/60 text-sm">Total Referrals</p>
                <p className="text-2xl font-bold text-white">{analytics.total_referrals}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-white/60 text-sm">This Month</p>
                <p className="text-2xl font-bold text-[#0FFFE2]">{analytics.monthly_referrals}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-white/60 text-sm">This Week</p>
                <p className="text-2xl font-bold text-[#FF7901]">{analytics.weekly_referrals}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-white/60 text-sm">Conversion Rate</p>
                <p className="text-2xl font-bold text-[#df1283]">{analytics.conversion_rate}%</p>
              </div>
            </div>

            {/* Chart */}
            <div className="p-4 bg-white/5 rounded-lg">
              <h4 className="text-white font-medium mb-4">Referrals (Last 30 Days)</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.daily_breakdown?.slice().reverse()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#ffffff40" 
                      tick={{ fill: '#ffffff60', fontSize: 10 }}
                      tickFormatter={(v) => v.slice(5)}
                    />
                    <YAxis stroke="#ffffff40" tick={{ fill: '#ffffff60' }} />
                    <Tooltip
                      contentStyle={{ background: '#1a0527', border: '1px solid #df1283', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="count" fill="#df1283" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Referrers */}
            <div className="p-4 bg-white/5 rounded-lg">
              <h4 className="text-white font-medium mb-4">Top Referrers</h4>
              {analytics.top_referrers?.length > 0 ? (
                <div className="space-y-2">
                  {analytics.top_referrers.map((referrer, idx) => (
                    <div key={referrer.user_id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-500 text-black' :
                          idx === 1 ? 'bg-gray-400 text-black' :
                          idx === 2 ? 'bg-orange-700 text-white' :
                          'bg-white/20 text-white'
                        }`}>
                          {idx + 1}
                        </span>
                        <div>
                          <p className="text-white text-sm">{referrer.name || referrer.email}</p>
                          <p className="text-white/50 text-xs">{referrer.email}</p>
                        </div>
                      </div>
                      <span className="text-[#0FFFE2] font-medium">{referrer.referral_count} referrals</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/50 text-center py-4">No referrers yet</p>
              )}
            </div>
          </div>
        )}

        {/* Winners Tab */}
        {activeTab === 'winners' && (
          <div className="space-y-6" data-testid="referral-winners-panel">
            {/* Draw Winner Section */}
            <div className="p-4 bg-gradient-to-br from-[#df1283]/10 to-[#FF7901]/10 rounded-lg border border-[#df1283]/30">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#FF7901]" /> Draw Winner
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-white/70 block mb-1">Select Campaign</label>
                  <select
                    value={selectedCampaignForWinner || ''}
                    onChange={(e) => setSelectedCampaignForWinner(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    <option value="">Choose campaign...</option>
                    {campaigns.map(c => (
                      <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/70 block mb-1">Number of Winners</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={winnerCount}
                    onChange={(e) => setWinnerCount(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={drawWinner}
                    className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
                    data-testid="draw-winner-btn"
                  >
                    <Trophy className="w-4 h-4 mr-2" /> Draw Winner
                  </Button>
                </div>
              </div>
              
              {/* Recent Winners */}
              {recentWinners.length > 0 && (
                <div className="mt-4 p-4 bg-[#0FFFE2]/10 rounded-lg border border-[#0FFFE2]/30">
                  <h4 className="text-[#0FFFE2] font-medium mb-2">Just Selected!</h4>
                  {recentWinners.map(winner => (
                    <div key={winner.user_id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-white">{winner.name || winner.email}</p>
                        <p className="text-white/50 text-sm">{winner.referral_count} referrals</p>
                      </div>
                      <Trophy className="w-5 h-5 text-yellow-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Campaign Winners List */}
            <div className="space-y-4">
              <h3 className="text-white font-medium">All Winners by Campaign</h3>
              {campaigns.filter(c => c.winners?.length > 0).length === 0 ? (
                <p className="text-white/50 text-center py-8">No winners selected yet</p>
              ) : (
                campaigns.filter(c => c.winners?.length > 0).map(campaign => (
                  <div key={campaign.campaign_id} className="p-4 bg-white/5 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-white font-medium">{campaign.name}</h4>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => notifyWinners(campaign.campaign_id)}
                        className="border-[#0FFFE2]/30 text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
                      >
                        <Send className="w-3 h-3 mr-1" /> Notify All
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {campaign.winners.map((winner, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-yellow-500" />
                            <span className="text-white">{winner.name || winner.email}</span>
                          </div>
                          <span className="text-white/50 text-sm">{winner.referral_count} referrals</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Email Templates Tab - Matching SystemEmailTemplates UI */}
        {activeTab === 'emails' && (
          <div className="space-y-6" data-testid="referral-emails-panel">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#df1283]" />
                  Referral Email Templates
                </h3>
                <p className="text-sm text-[#FDF2F8]/50 mt-1">
                  Customize automated emails sent during referral campaigns
                </p>
              </div>
              <Button
                onClick={fetchEmailTemplates}
                variant="outline"
                className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-[#4A0A66] flex items-center gap-3">
                <Gift className="w-5 h-5 text-[#df1283]" />
                <span className="text-white font-semibold">Referral Emails</span>
                <span className="text-[#FDF2F8]/50 text-sm">({emailTemplates.length})</span>
              </div>
              
              <div className="divide-y divide-[#4A0A66]/30">
                {emailTemplates.map(template => (
                  <div
                    key={template.template_type}
                    className={`px-6 py-4 hover:bg-[#4A0A66]/10 transition-colors ${!template.enabled ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${template.enabled ? 'bg-[#4A0A66]/30' : 'bg-gray-600/30'}`}>
                          <Mail className={`w-5 h-5 ${template.enabled ? 'text-[#df1283]' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium">{template.name}</h4>
                            {!template.enabled && (
                              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                                Disabled
                              </span>
                            )}
                          </div>
                          <p className="text-[#FDF2F8]/50 text-sm">{template.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Toggle Switch */}
                        <button
                          onClick={() => toggleEmailTemplate(template)}
                          disabled={togglingTemplate === template.template_type}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            template.enabled ? 'bg-green-500' : 'bg-gray-600'
                          }`}
                          title={template.enabled ? 'Click to disable' : 'Click to enable'}
                        >
                          {togglingTemplate === template.template_type ? (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <RefreshCw className="w-4 h-4 animate-spin text-white" />
                            </span>
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                template.enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          )}
                        </button>
                        <Button
                          onClick={() => window.open(`${API_URL}/api/admin/referral/email-templates/${template.template_type}/preview`, '_blank')}
                          size="sm"
                          variant="outline"
                          className="border-[#0FFFE2]/50 text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
                          title="Preview how this email looks when sent"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          onClick={() => setTestEmailModal(template.template_type)}
                          size="sm"
                          variant="outline"
                          className="border-[#FF7901]/50 text-[#FF7901] hover:bg-[#FF7901]/10"
                          title="Send a test email to verify in your inbox"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Test
                        </Button>
                        <Button
                          onClick={() => setEditingTemplate(template)}
                          size="sm"
                          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                    
                    {/* Merge Tags Preview */}
                    {template.variables && template.variables.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-[#FDF2F8]/40 text-xs">Available tags:</span>
                        {template.variables.map(tag => (
                          <code 
                            key={tag}
                            className="text-xs bg-[#0FFFE2]/10 text-[#0FFFE2] px-2 py-0.5 rounded"
                          >
                            {`{{${tag}}}`}
                          </code>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a0527] border border-[#df1283]/30 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-bold text-white">
                {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-white/70 block mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  placeholder="August iPad Giveaway"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 block mb-1">Prize Name</label>
                <input
                  type="text"
                  value={campaignForm.prize_name}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, prize_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  placeholder="iPad Pro 11"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 block mb-1">Prize Image</label>
                <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center">
                  {campaignForm.prize_image_url ? (
                    <div className="relative inline-block">
                      <img src={campaignForm.prize_image_url} alt="Prize" className="h-24 rounded-lg" />
                      <button
                        onClick={() => setCampaignForm(prev => ({ ...prev, prize_image_url: '' }))}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <Upload className="w-8 h-8 mx-auto text-white/40 mb-2" />
                      <span className="text-sm text-white/60">Click to upload prize image</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePrizeImageUpload(e)}
                      />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 block mb-1">Referrals Needed to Qualify</label>
                <input
                  type="number"
                  min="1"
                  value={campaignForm.referrals_needed}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, referrals_needed: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={campaignForm.start_date}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 block mb-1">Custom Share Message (optional)</label>
                <textarea
                  value={campaignForm.custom_share_message}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, custom_share_message: e.target.value }))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
                  rows={2}
                  placeholder="Check out this amazing journal app!"
                />
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCampaignModal(false);
                  setEditingCampaign(null);
                }}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCampaign}
                className="bg-[#df1283] hover:bg-[#F02D9A]"
              >
                {editingCampaign ? 'Update' : 'Create'} Campaign
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Editor Modal - Matching SystemEmailTemplates UI */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl w-full max-w-7xl my-8 border border-[#4A0A66] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-[#df1283]" />
                <div>
                  <h2 className="text-xl font-bold text-white">{editingTemplate.name}</h2>
                  <p className="text-sm text-[#FDF2F8]/50">{editingTemplate.description}</p>
                </div>
              </div>
              <button
                onClick={() => setEditingTemplate(null)}
                className="p-2 hover:bg-[#4A0A66]/30 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#FDF2F8]/70" />
              </button>
            </div>
            
            {/* Modal Content - Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
              {/* Left Column - Editor */}
              <div className="space-y-4">
                {/* Enable/Disable Toggle */}
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30 flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-white">Template Enabled</label>
                    <p className="text-xs text-[#FDF2F8]/40">When disabled, this email will not be sent</p>
                  </div>
                  <Switch
                    checked={editingTemplate.enabled}
                    onCheckedChange={(checked) => setEditingTemplate(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>
                
                {/* Subject Line */}
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                  <label className="block text-sm font-medium text-white mb-2">Subject Line</label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Email subject..."
                    className="w-full px-3 py-2 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg text-white placeholder:text-[#FDF2F8]/30"
                  />
                </div>
                
                {/* Merge Tags */}
                {editingTemplate.variables && editingTemplate.variables.length > 0 && (
                  <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                    <label className="block text-sm font-medium text-white mb-2">Available Merge Tags</label>
                    <p className="text-xs text-[#FDF2F8]/40 mb-3">Click to copy, then paste into your email</p>
                    <div className="flex flex-wrap gap-2">
                      {editingTemplate.variables.map(tag => (
                        <button
                          key={tag}
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${tag}}}`);
                            toast.success(`Copied {{${tag}}}`);
                          }}
                          className="text-xs bg-[#0FFFE2]/10 text-[#0FFFE2] px-3 py-1.5 rounded hover:bg-[#0FFFE2]/20 transition-colors flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          {`{{${tag}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Email Content - Block Editor */}
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                  <label className="block text-sm font-medium text-white mb-2">Email Content</label>
                  <p className="text-xs text-[#FDF2F8]/40 mb-3">
                    Use the block editor to design your email
                  </p>
                  <div className="bg-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
                    <EmailBlockBuilder
                      value={editingTemplate.body_html || ''}
                      onChange={(html) => setEditingTemplate(prev => ({ ...prev, body_html: html }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Right Column - Preview */}
              <div className="space-y-4">
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                  {/* Preview Header with Device Toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-white">LIVE PREVIEW</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => window.open(`${API_URL}/api/admin/referral/email-templates/${editingTemplate.template_type}/preview`, '_blank')}
                        className="text-xs bg-[#0FFFE2]/20 text-[#0FFFE2] px-3 py-1.5 rounded hover:bg-[#0FFFE2]/30 transition-colors flex items-center gap-1"
                        title="View exactly how this email will look when sent"
                      >
                        <Eye className="w-3 h-3" />
                        View Full Email
                      </button>
                      <button
                        onClick={() => setTestEmailModal(editingTemplate.template_type)}
                        className="text-xs bg-[#FF7901]/20 text-[#FF7901] px-3 py-1.5 rounded hover:bg-[#FF7901]/30 transition-colors flex items-center gap-1"
                        title="Send a test email to your inbox"
                      >
                        <Send className="w-3 h-3" />
                        Send Test
                      </button>
                      <div className="flex items-center gap-1 bg-[#4A0A66]/30 rounded-lg p-1">
                        <button
                          onClick={() => setEmailPreviewDevice('mobile')}
                          className={`p-2 rounded transition-colors ${emailPreviewDevice === 'mobile' ? 'bg-[#df1283] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                          title="Mobile Preview"
                        >
                          <Smartphone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEmailPreviewDevice('tablet')}
                          className={`p-2 rounded transition-colors ${emailPreviewDevice === 'tablet' ? 'bg-[#df1283] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                          title="Tablet Preview"
                        >
                          <Tablet className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEmailPreviewDevice('desktop')}
                          className={`p-2 rounded transition-colors ${emailPreviewDevice === 'desktop' ? 'bg-[#df1283] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                          title="Desktop Preview"
                        >
                          <Monitor className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview Container */}
                  <div className="bg-[#4A0A66]/20 rounded-lg p-4 min-h-[500px]">
                    {renderReferralEmailPreview()}
                  </div>
                  
                  <p className="text-xs text-[#FDF2F8]/40 text-center mt-3">
                    This is a content preview. Click &quot;View Full Email&quot; to see the complete email with header, logo, and footer.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#4A0A66]">
              <Button
                onClick={() => setEditingTemplate(null)}
                variant="outline"
                className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
              >
                Cancel
              </Button>
              <Button
                onClick={() => updateEmailTemplate(editingTemplate.template_type, {
                  subject: editingTemplate.subject,
                  body_html: editingTemplate.body_html,
                  enabled: editingTemplate.enabled
                })}
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Template
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Email Modal - Matching SystemEmailTemplates */}
      {testEmailModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl w-full max-w-md border border-[#4A0A66] shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]">
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-[#FF7901]" />
                <h2 className="text-lg font-bold text-white">Send Test Email</h2>
              </div>
              <button
                onClick={() => { setTestEmailModal(null); setTestEmailAddress(''); }}
                className="p-2 hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#FDF2F8]/50" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#FDF2F8]/70">
                Send a test email to verify how the <strong className="text-[#0FFFE2]">{emailTemplates.find(t => t.template_type === testEmailModal)?.name || testEmailModal}</strong> template looks in an actual inbox.
              </p>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/50 mb-2">Recipient Email *</label>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="your-email@example.com"
                  className="w-full px-3 py-2 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg text-white"
                  autoFocus
                />
              </div>
              
              <p className="text-xs text-[#FDF2F8]/40">
                The test email will include [TEST] in the subject line and use sample data for all merge tags.
              </p>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setTestEmailModal(null); setTestEmailAddress(''); }}
                  className="border-[#4A0A66] text-[#FDF2F8]/70"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTestEmail}
                  disabled={!testEmailAddress || sendingTest}
                  className="bg-gradient-to-r from-[#FF7901] to-[#df1283] hover:opacity-90"
                >
                  {sendingTest ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralManagement;
